(() => {
  const select = (selector, context = document) => context.querySelector(selector);
  const selectAll = (selector, context = document) => [...context.querySelectorAll(selector)];
  const axes = ['warm', 'fresh', 'woody', 'smoky', 'coastal', 'bright', 'dark', 'spicy'];
  const axisLabels = { warm: 'warm', fresh: 'fresh', woody: 'woodsy', smoky: 'smoky', coastal: 'coastal', bright: 'bright', dark: 'dark', spicy: 'spicy' };
  const scentProfiles = {
    big_wick: { warm: 5, fresh: 0, woody: 4, smoky: 1, coastal: 0, bright: 0, dark: 3, spicy: 2 },
    morning_wood: { warm: 1, fresh: 2, woody: 5, smoky: 0, coastal: 0, bright: 1, dark: 1, spicy: 0 },
    low_tide: { warm: 2, fresh: 5, woody: 1, smoky: 0, coastal: 5, bright: 2, dark: 0, spicy: 0 },
    shuck: { warm: 3, fresh: 0, woody: 2, smoky: 2, coastal: 0, bright: 0, dark: 5, spicy: 5 },
    salty_secret: { warm: 1, fresh: 5, woody: 0, smoky: 0, coastal: 3, bright: 5, dark: 0, spicy: 1 },
    blowin_smoke: { warm: 4, fresh: 0, woody: 4, smoky: 5, coastal: 0, bright: 0, dark: 4, spicy: 3 }
  };
  const handleFromUrl = value => {
    if (!value) return '';
    try {
      const url = new URL(value, window.location.origin);
      const match = url.pathname.match(/\/products\/([^/?#]+)/i);
      return match ? decodeURIComponent(match[1]) : '';
    } catch (error) {
      return '';
    }
  };
  const resultMeta = result => result ? {
    key: result.key || '',
    handle: handleFromUrl(result.url),
    title: result.title || '',
    url: result.url || '',
    destination: result.url || '',
    notes: result.notes || '',
    tagline: result.tagline || ''
  } : null;
  const parseVector = value => {
    const vector = Object.fromEntries(axes.map(axis => [axis, 0]));
    String(value || '').split(',').forEach(pair => {
      const [key, amount] = pair.split(':');
      const axis = key?.trim();
      if (axis && Object.prototype.hasOwnProperty.call(vector, axis)) vector[axis] = Number(amount || 0);
    });
    return vector;
  };
  const cosineSimilarity = (preference, profile) => {
    let dot = 0;
    let preferenceMagnitude = 0;
    let profileMagnitude = 0;
    axes.forEach(axis => {
      const left = Number(preference[axis] || 0);
      const right = Number(profile[axis] || 0);
      dot += left * right;
      preferenceMagnitude += left * left;
      profileMagnitude += right * right;
    });
    if (!preferenceMagnitude || !profileMagnitude) return -Infinity;
    return dot / (Math.sqrt(preferenceMagnitude) * Math.sqrt(profileMagnitude));
  };
  const joinNatural = values => {
    if (values.length <= 1) return values[0] || '';
    if (values.length === 2) return `${values[0]} and ${values[1]}`;
    return `${values.slice(0, -1).join(', ')}, and ${values.at(-1)}`;
  };
  const rememberQuizMatch = (primary, runner) => {
    const experience = window.GooeyExperience;
    if (!experience?.state) return;
    const snapshot = experience.state.get();
    const primaryMeta = resultMeta(primary);
    const runnerMeta = resultMeta(runner);
    const existing = Array.isArray(snapshot.scentInterests) ? snapshot.scentInterests : [];
    const additions = [primaryMeta, runnerMeta].filter(Boolean);
    const scentInterests = [...existing];
    additions.forEach(item => {
      const index = scentInterests.findIndex(existingItem => (item.handle && existingItem.handle === item.handle) || existingItem.title === item.title);
      if (index >= 0) scentInterests[index] = item;
      else scentInterests.push(item);
    });
    experience.state.patch({
      currentModule: 'scent-quiz',
      scentInterests,
      scentMatch: { primary: primaryMeta, runner: runnerMeta }
    }, { module: 'scent-quiz', action: 'result-view' });
  };
  const rememberProductChoice = (result, action) => {
    const experience = window.GooeyExperience;
    if (!experience?.state || !result) return;
    const snapshot = experience.state.get();
    const recommendation = resultMeta(result);
    const room = snapshot.currentRoom || snapshot.continuation?.room || null;
    const situation = snapshot.currentSituation || snapshot.continuation?.situation || null;
    experience.state.patch({
      currentModule: 'scent-quiz',
      currentRoom: room,
      currentSituation: situation,
      currentProblem: situation?.problem || snapshot.currentProblem || room?.problem || null,
      selectedRecommendation: recommendation,
      continuation: {
        source: 'scent-quiz',
        mode: 'product',
        room,
        situation,
        recommendation,
        destination: result.url || ''
      }
    }, { module: 'scent-quiz', action });
  };
  const initQuiz = quiz => {
    if (quiz.dataset.initialized) return;
    quiz.dataset.initialized = 'true';
    const resultData = select('[data-scent-results]', quiz);
    const questions = selectAll('[data-quiz-question]', quiz);
    const stage = select('[data-quiz-stage]', quiz);
    const resultPanel = select('[data-quiz-result]', quiz);
    const backButton = select('[data-quiz-back]', quiz);
    const restartButton = select('[data-quiz-restart]', quiz);
    const shareButton = select('[data-quiz-share]', quiz);
    const progressLabel = select('[data-quiz-progress-label]', quiz);
    const progressBar = select('[data-quiz-progress-bar]', quiz);
    if (!resultData || !questions.length || !stage || !resultPanel) return;
    let results;
    try { results = JSON.parse(resultData.textContent); }
    catch (error) { console.error('Unable to load scent quiz results.', error); return; }
    const resultOrder = Object.keys(results);
    const answers = new Array(questions.length).fill(null);
    let currentQuestion = 0;
    let currentResult = null;
    let currentRunner = null;
    const showQuestion = (index, shouldFocus = true) => {
      currentQuestion = Math.max(0, Math.min(index, questions.length - 1));
      questions.forEach((question, questionIndex) => {
        question.hidden = questionIndex !== currentQuestion;
        selectAll('[data-quiz-answer]', question).forEach(button => button.classList.toggle('is-selected', answers[questionIndex]?.button === button));
      });
      stage.hidden = false;
      resultPanel.hidden = true;
      if (backButton) backButton.hidden = currentQuestion === 0;
      if (progressLabel) progressLabel.textContent = `Question ${currentQuestion + 1} of ${questions.length}`;
      if (progressBar) progressBar.style.setProperty('--quiz-progress', `${((currentQuestion + 1) / questions.length) * 100}%`);
      if (shouldFocus) select('[data-quiz-answer]', questions[currentQuestion])?.focus({ preventScroll: true });
    };
    const preferenceVector = () => {
      const total = Object.fromEntries(axes.map(axis => [axis, 0]));
      answers.forEach(answer => {
        if (!answer) return;
        axes.forEach(axis => { total[axis] += Number(answer.vector[axis] || 0); });
      });
      return total;
    };
    const rankResults = () => {
      const preference = preferenceVector();
      return resultOrder.filter(key => scentProfiles[key])
        .map((key, order) => ({ key, order, score: cosineSimilarity(preference, scentProfiles[key]), ...results[key] }))
        .sort((a, b) => b.score - a.score || a.order - b.order);
    };
    const preferenceSummary = () => {
      const preference = preferenceVector();
      const strongest = axes.filter(axis => preference[axis] > 0).sort((a, b) => preference[b] - preference[a]).slice(0, 3).map(axis => axisLabels[axis]);
      return strongest.length ? `Your answers leaned ${joinNatural(strongest)}.` : '';
    };
    const setText = (selector, value) => {
      const node = select(selector, quiz);
      if (node) node.textContent = value || '';
    };
    const renderMedia = result => {
      const host = select('[data-result-media]', quiz);
      if (!host) return;
      host.replaceChildren();
      if (result.image) {
        const image = document.createElement('img');
        image.src = result.image;
        image.alt = result.imageAlt || result.title || 'Recommended candle';
        image.loading = 'eager';
        host.appendChild(image);
      } else {
        const placeholder = document.createElement('div');
        placeholder.className = 'editor-placeholder';
        const label = document.createElement('span');
        label.textContent = result.title || 'Your scent';
        placeholder.appendChild(label);
        host.appendChild(placeholder);
      }
    };
    const showResult = () => {
      const ranked = rankResults();
      const primary = ranked[0];
      const runner = ranked[1] || ranked[0];
      if (!primary) return;
      currentResult = primary;
      currentRunner = runner;
      renderMedia(primary);
      setText('[data-result-title]', primary.title);
      setText('[data-result-match]', preferenceSummary());
      setText('[data-result-tagline]', primary.tagline);
      setText('[data-result-notes]', primary.notes);
      setText('[data-result-copy]', primary.copy);
      const priceNode = select('[data-result-price]', quiz);
      if (priceNode) {
        priceNode.textContent = primary.price ? `${primary.priceVaries ? 'From ' : ''}${primary.price}` : '';
        priceNode.hidden = !primary.price;
      }
      setText('[data-runner-title]', runner.title);
      setText('[data-runner-notes]', runner.notes);
      const primaryLink = select('[data-result-link]', quiz);
      if (primaryLink) { primaryLink.href = primary.url || '/collections/all'; primaryLink.setAttribute('aria-label', `Meet ${primary.title}`); }
      const runnerLink = select('[data-runner-link]', quiz);
      if (runnerLink) { runnerLink.href = runner.url || '/collections/all'; runnerLink.setAttribute('aria-label', `Meet ${runner.title}`); }
      rememberQuizMatch(primary, runner);
      stage.hidden = true;
      resultPanel.hidden = false;
      resultPanel.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'center' });
    };
    questions.forEach((question, questionIndex) => {
      selectAll('[data-quiz-answer]', question).forEach(button => {
        button.addEventListener('click', () => {
          answers[questionIndex] = { button, vector: parseVector(button.dataset.vector) };
          selectAll('[data-quiz-answer]', question).forEach(answer => answer.classList.toggle('is-selected', answer === button));
          window.setTimeout(() => {
            if (questionIndex < questions.length - 1) showQuestion(questionIndex + 1, true);
            else showResult();
          }, 180);
        });
      });
    });
    backButton?.addEventListener('click', () => showQuestion(currentQuestion - 1));
    restartButton?.addEventListener('click', () => {
      answers.fill(null);
      currentResult = null;
      currentRunner = null;
      const status = select('[data-share-status]', quiz);
      if (status) status.hidden = true;
      window.GooeyExperience?.state?.patch({ scentMatch: null }, { module: 'scent-quiz', action: 'restart' });
      showQuestion(0, true);
      quiz.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'start' });
    });
    select('[data-result-link]', quiz)?.addEventListener('click', () => rememberProductChoice(currentResult, 'primary-product-cta'));
    select('[data-runner-link]', quiz)?.addEventListener('click', () => rememberProductChoice(currentRunner, 'runner-product-cta'));
    shareButton?.addEventListener('click', async () => {
      if (!currentResult) return;
      const shareText = `I got ${currentResult.title}. This explains more than the candle.`;
      const shareData = { title: `My Gooey Duck scent: ${currentResult.title}`, text: shareText, url: currentResult.url || window.location.href };
      const status = select('[data-share-status]', quiz);
      try {
        if (navigator.share) await navigator.share(shareData);
        else {
          await navigator.clipboard.writeText(`${shareText} ${shareData.url}`);
          if (status) { status.textContent = 'Result copied. Use it irresponsibly.'; status.hidden = false; }
        }
      } catch (error) {
        if (error?.name !== 'AbortError' && status) { status.textContent = 'Sharing failed. The room still knows what you picked.'; status.hidden = false; }
      }
    });
    showQuestion(0, false);
  };
  const initAll = root => {
    if (root instanceof Element && root.matches('[data-scent-quiz]')) initQuiz(root);
    selectAll('[data-scent-quiz]', root).forEach(initQuiz);
  };
  document.addEventListener('shopify:section:load', event => initAll(event.target));
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initAll(document));
  else initAll(document);
})();
